import React, { useState } from "react";
import { Modal, Radio, Input, message } from "antd";
import {
  InfoCircleOutlined,
  ExclamationCircleOutlined,
  CheckOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { cancelSubscription } from "../subscription.service";
import { CancellationReason } from "../subscription.types";

const { TextArea } = Input;

type Step = "reason" | "confirm" | "success";

interface CancelSubscriptionModalProps {
  open: boolean;
  /** Date access is retained until — the current period end pre-cancel, the real cancellation date after. */
  accessUntil: string;
  onClose: () => void;
  /** Fired once the cancellation succeeds so the page can reflect the new state. */
  onCancelled: (accessUntil: string) => void;
}

const CancelSubscriptionModal: React.FC<CancelSubscriptionModalProps> = ({
  open,
  accessUntil,
  onClose,
  onCancelled,
}) => {
  const { t } = useTranslation("subscription");
  const REASONS = [
    { key: "no_longer_use", label: t("cancelModal.reasons.noLongerUse") },
    { key: "privacy", label: t("cancelModal.reasons.privacy") },
    { key: "not_satisfied", label: t("cancelModal.reasons.notSatisfied") },
    { key: "other", label: t("cancelModal.reasons.other") },
  ];
  const [step, setStep] = useState<Step>("reason");
  const [reasonKey, setReasonKey] = useState<string>("");
  const [reasonOther, setReasonOther] = useState("");
  const [improvement, setImprovement] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [confirmedAccessUntil, setConfirmedAccessUntil] = useState(accessUntil);

  const reset = () => {
    setStep("reason");
    setReasonKey("");
    setReasonOther("");
    setImprovement("");
    setSubmitting(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const canProceed =
    reasonKey !== "" && (reasonKey !== "other" || reasonOther.trim() !== "");

  const handleConfirmCancel = async () => {
    setSubmitting(true);
    const reason: CancellationReason = {
      reasonKey,
      reasonOther: reasonKey === "other" ? reasonOther : undefined,
      improvement: improvement || undefined,
    };
    try {
      const { accessUntil: until } = await cancelSubscription(reason);
      setConfirmedAccessUntil(until);
      setStep("success");
      onCancelled(until);
    } catch (e: any) {
      message.error(
        e?.response?.data?.detail || t("cancelModal.cancelError"),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      footer={null}
      centered
      width={480}
      className="sub-modal"
      destroyOnClose
    >
      {step === "reason" && (
        <>
          <h3 className="sub-modal-title">{t("cancelModal.title")}</h3>
          <p className="sub-modal-sub">{t("cancelModal.subtitle")}</p>

          <Radio.Group
            className="sub-reason-list"
            value={reasonKey}
            onChange={(e) => setReasonKey(e.target.value)}
          >
            {REASONS.map((r) => (
              <Radio key={r.key} value={r.key}>
                {r.label}
              </Radio>
            ))}
          </Radio.Group>

          {reasonKey === "other" && (
            <Input
              style={{ marginTop: 10 }}
              placeholder={t("cancelModal.otherPlaceholder")}
              value={reasonOther}
              onChange={(e) => setReasonOther(e.target.value)}
            />
          )}

          <div className="sub-field-label">
            {t("cancelModal.improvementLabel")}
          </div>
          <TextArea
            placeholder={t("cancelModal.improvementPlaceholder")}
            maxLength={100}
            rows={3}
            value={improvement}
            onChange={(e) => setImprovement(e.target.value)}
          />
          <div className="sub-char-count">
            {t("cancelModal.charCount", { used: improvement.length })}
          </div>

          <div className="sub-note">
            <InfoCircleOutlined className="ic" />
            <span>
              {t("cancelModal.noteBefore")} <strong>{accessUntil}</strong>
              {t("cancelModal.noteAfter")}
            </span>
          </div>

          <div className="sub-modal-actions">
            <button className="sub-btn sub-btn-outline" onClick={handleClose}>
              {t("cancelModal.keepPlan")}
            </button>
            <button
              className="sub-btn sub-btn-primary"
              disabled={!canProceed}
              onClick={() => setStep("confirm")}
            >
              {t("cancelModal.cancelButton")}
            </button>
          </div>
        </>
      )}

      {step === "confirm" && (
        <>
          <div className="sub-confirm-title">
            <ExclamationCircleOutlined /> {t("cancelModal.title")}
          </div>
          <p className="sub-confirm-body">
            {t("cancelModal.confirm.body")}
            <strong>{t("cancelModal.confirm.confirmQuestion")}</strong>
          </p>
          <div className="sub-modal-actions">
            <button
              className="sub-btn sub-btn-outline"
              onClick={() => setStep("reason")}
            >
              {t("cancelModal.confirm.noKeepPlan")}
            </button>
            <button
              className="sub-btn sub-btn-danger"
              disabled={submitting}
              onClick={handleConfirmCancel}
            >
              {submitting
                ? t("cancelModal.confirm.cancelling")
                : t("cancelModal.confirm.yesCancel")}
            </button>
          </div>
        </>
      )}

      {step === "success" && (
        <>
          <div className="sub-success-check">
            <CheckOutlined />
          </div>
          <h3 className="sub-modal-title">{t("cancelModal.success.title")}</h3>
          <p className="sub-modal-sub">{t("cancelModal.success.subtitle")}</p>
          <div className="sub-summary">
            <div className="sub-summary-row">
              <span className="k">{t("cancelModal.success.accessUntilLabel")}</span>
              <span className="v">{confirmedAccessUntil}</span>
            </div>
            <div className="sub-summary-row">
              <span className="k">{t("cancelModal.success.nextChargeLabel")}</span>
              <span className="v">{t("cancelModal.success.none")}</span>
            </div>
          </div>
          <div className="sub-note danger">
            <WarningOutlined className="ic" />
            <span>
              {t("cancelModal.success.dangerBefore")}{" "}
              <strong>{confirmedAccessUntil}</strong>
              {t("cancelModal.success.dangerAfter")}
            </span>
          </div>
          <div className="sub-modal-actions">
            <button className="sub-btn sub-btn-primary" onClick={handleClose}>
              {t("cancelModal.success.done")}
            </button>
          </div>
        </>
      )}
    </Modal>
  );
};

export default CancelSubscriptionModal;
